import React from 'react';
import '../styles.css';
import { Helmet } from 'react-helmet-async';


const PrivacyPolicy = () => (
    <>
        <Helmet>
		<script type="application/ld+json">
			{JSON.stringify({
			  "@context": "https://schema.org",
			  "@type": "WebPage",
			  "name": "Privacy Policy",
			  "url": "https://hermesreads.com/privacy-policy",
			  "description": "Learn how HermesReads collects, stores, and processes user data including advertising practices, analytics, and user rights under GDPR and CCPA."
			})}
			</script>
						<meta name="robots" content="noindex, follow" />
        </Helmet>

        <main className="privacy-container" style={{ maxWidth: '800px', margin: '2rem auto', padding: '2rem', backgroundColor: 'var(--light-gray)', color: 'var(--text-color)', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)' }}>
            <h1 style={{ color: 'var(--highlight-color)', textAlign: 'center', marginBottom: '1.5rem' }}>Privacy Policy</h1>
            <p>Last updated: [Insert Date]</p>

            <h2 style={{ color: 'var(--primary-color)', marginTop: '1.5rem' }}>1. Introduction</h2>
            <p>Welcome to <strong>HermesReads</strong>. Your privacy is important to us, and we are committed to safeguarding your personal data while providing an optimal reading experience. This Privacy Policy explains how we collect, use, and share information, including your interactions with our website, advertising partners, and analytics providers.</p>
            <p>This Privacy Policy complies with applicable data protection laws, including:</p>
            <ul>
                <li>General Data Protection Regulation (GDPR) for users in the European Economic Area (EEA),</li>
                <li>California Consumer Privacy Act (CCPA) for users in California, and</li>
                <li>Children's Online Privacy Protection Act (COPPA) where applicable.</li>
            </ul>
            <p>By accessing or using HermesReads, you consent to the data practices described in this policy. If you do not agree with our policies and practices, please discontinue use of our website.</p>

            <h2 style={{ color: 'var(--primary-color)', marginTop: '1.5rem' }}>2. Information We Collect</h2>
            <p>We collect and process various types of information to improve the HermesReads platform, enhance user experience, and ensure compliance with relevant regulations. The data we collect falls into the following categories:</p>
            <h3>2.1 Personal Information</h3>
            <p>We collect personal data that you voluntarily provide to us, including but not limited to:</p>
            <ul>
                <li><strong>Contact Information:</strong> When you contact us via forms or email, we may collect your name, email address, and any additional information you provide.</li>
                <li><strong>Account Information:</strong> If user accounts are introduced in the future, we may collect usernames, passwords, and profile preferences.</li>
            </ul>
            <h3>2.2 Usage Data</h3>
            <p>We collect anonymized data on how users interact with our website to analyze trends and improve our services. This may include:</p>
            <ul>
                <li>Pages visited, session duration, and navigation patterns.</li>
                <li>Device details, such as browser type, screen resolution, and operating system.</li>
                <li>Geographic location (based on IP address) to personalize content and comply with regional regulations.</li>
                <li>Interaction data, such as clicks, scrolling behavior, and idle times.</li>
            </ul>
            <h3>2.3 Cookies and Tracking Technologies</h3>
            <p>HermesReads uses cookies and other tracking technologies to enhance user experience and for analytical purposes. The types of cookies we use include:</p>
            <ul>
                <li><strong>Essential Cookies:</strong> Necessary for website functionality, such as page navigation.</li>
                <li><strong>Analytics Cookies:</strong> Help us understand website usage patterns and improve performance.</li>
                <li><strong>Advertising Cookies:</strong> Used by third-party advertisers, such as Google Ads, to provide personalized ads.</li>
            </ul>

            <h2 style={{ color: 'var(--primary-color)', marginTop: '1.5rem' }}>3. How We Use Your Information</h2>
            <p>We use the data we collect for various purposes, ensuring compliance with GDPR, CCPA, and other applicable privacy laws. Our primary uses of your information include:</p>
            <h3>3.1 Providing and Improving Our Services</h3>
            <p>We process your data to:</p>
            <ul>
                <li>Enhance website functionality and optimize performance.</li>
                <li>Personalize your reading experience by recommending books and content.</li>
                <li>Troubleshoot technical issues and maintain security.</li>
            </ul>
            <h3>3.2 Analytics and Performance Monitoring</h3>
            <p>We collect and analyze aggregated, anonymized data to:</p>
            <ul>
                <li>Understand user engagement with our content.</li>
                <li>Improve website navigation and design.</li>
                <li>Measure the effectiveness of our services.</li>
            </ul>
            <h3>3.3 Advertising and Marketing</h3>
            <p>To support HermesReads, we collaborate with third-party advertisers (e.g., Google Ads). Your data may be used for:</p>
            <ul>
                <li>Displaying relevant ads based on your interests.</li>
                <li>Measuring the effectiveness of advertising campaigns.</li>
                <li>Providing anonymized insights to our advertising partners.</li>
            </ul>
            <p>If you prefer not to receive personalized advertising, you can opt-out via:</p>
                <li><a href="https://adssettings.google.com/authenticated" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>.</li>
                <li>Industry-wide opt-out tools: <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">About Ads Opt-Out</a>.</li>
          
            <h3>3.4 Compliance with Legal Obligations</h3>
            <p>We may process your data to:</p>
            <ul>
                <li>Enforce our Terms of Service.</li>
                <li>Prevent fraud, abuse, or security breaches.</li>
                <li>Comply with legal requirements (e.g., court orders, law enforcement requests).</li>
            </ul>

            <h2 style={{ color: 'var(--primary-color)', marginTop: '1.5rem' }}>4. Sharing Your Information</h2>
            <p>We take your privacy seriously and do not sell your personal data. However, we may share certain types of information with trusted third parties under the following circumstances:</p>
            <h3>4.1 Third-Party Service Providers</h3>
            <p>We work with third-party companies that help us operate, analyze, and improve HermesReads. These service providers may have access to certain data strictly for the purposes of:</p>
            <ul>
                <li>Website analytics (e.g., Google Analytics)</li>
                <li>Advertising and marketing (e.g., Google Ads)</li>
                <li>Hosting and security services (e.g., cloud storage providers)</li>
            </ul>
            <p>These companies are obligated to process your data securely and in compliance with applicable laws.</p>
            <h3>4.2 Advertising and Analytics Partners</h3>
            <p>To provide personalized ads and improve our services, we may share anonymized, aggregated data with:</p>
            <ul>
                <li><strong>Google Ads & Google Analytics:</strong> Your interaction with the website may be used to display relevant ads. See how Google uses data: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">Google Partner Sites Policy</a>.</li>
                <li>Other ad networks and analytics providers that help us understand engagement trends.</li>
            </ul>
            <p>Users can manage ad preferences or opt-out via:</p>
            <ul>
                <li><a href="https://adssettings.google.com/authenticated" target="_blank" rel="noopener noreferrer">Google Ads Settings</a></li>
                <li><a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer">Network Advertising Initiative Opt-Out</a></li>
            </ul>
            <h3>4.3 Legal Compliance and Security</h3>
            <p>We may disclose user information if required to:</p>
            <ul>
                <li>Comply with legal obligations (e.g., law enforcement requests, court orders).</li>
                <li>Protect our rights, safety, and the integrity of our services.</li>
                <li>Prevent fraud or security threats.</li>
            </ul>
            <h3>4.4 Business Transfers</h3>
            <p>If HermesReads is acquired, merged, or undergoes a business transition, user information may be transferred to the new entity under the same privacy principles.</p>

            <h2 style={{ color: 'var(--primary-color)', marginTop: '1.5rem' }}>5. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar tracking technologies to enhance user experience, analyze website performance, and support advertising. By using HermesReads, you consent to our use of these technologies unless you opt out.</p>
            <h3>5.1 What Are Cookies?</h3>
            <p>Cookies are small text files stored on your device that help improve website functionality. HermesReads uses the following types of cookies:</p>
            <ul>
                <li><strong>Essential Cookies:</strong> Required for core website functions, such as page navigation and security.</li>
                <li><strong>Analytics Cookies:</strong> Help us track usage patterns and improve content based on user behavior.</li>
                <li><strong>Advertising Cookies:</strong> Used by third-party advertisers (e.g., Google Ads) to deliver personalized ads.</li>
            </ul>
            <h3>5.2 Other Tracking Technologies</h3>
            <p>In addition to cookies, we use:</p>
            <ul>
                <li><strong>Session Storage & Local Storage:</strong> To remember preferences during your visit.</li>
                <li><strong>Google Analytics:</strong> To analyze traffic and optimize performance.</li>
                <li><strong>Page and Scroll Tracking:</strong> To measure user engagement.</li>
            </ul>
            <h3>5.3 Managing Cookies and Opting Out</h3>
            <p>You can control how cookies are used on HermesReads through:</p>
            <ul>
                <li>Our <a href="/#">Consent Settings</a> when you open the page, where you can enable or disable tracking features.</li>
                <li>Browser Settings, where you can block or delete cookies.</li>
                <li>Third-Party Tools, such as:
                    <ul>
                        <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-Out</a></li>
                        <li><a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer">Network Advertising Initiative Opt-Out</a></li>
                    </ul>
                </li>
            </ul>
            <p>Disabling cookies may impact certain website functionalities, such as personalized recommendations and ad preferences.</p>

            <h2 style={{ color: 'var(--primary-color)', marginTop: '1.5rem' }}>6. Your Rights</h2>
            <p>Depending on your location, you have specific rights regarding your personal data under laws such as the General Data Protection Regulation (GDPR) and the California Consumer Privacy Act (CCPA).</p>
            <h3>6.1 Rights Under GDPR (EU Users)</h3>
            <p>If you are in the European Economic Area (EEA), you have the following rights:</p>
            <ul>
                <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Right to Rectification:</strong> Request corrections to inaccurate or incomplete information.</li>
                <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of your data, subject to legal obligations.</li>
                <li><strong>Right to Restrict Processing:</strong> Request limited use of your data in certain circumstances.</li>
                <li><strong>Right to Data Portability:</strong> Receive a copy of your data in a structured format.</li>
                <li><strong>Right to Object:</strong> Object to the use of your data for marketing or analytics.</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw previously given consent at any time.</li>
            </ul>
            <p>To exercise these rights, contact us via the contact form in the website header or at our designated data protection email (if applicable).</p>
            <h3>6.2 Rights Under CCPA (California Users)</h3>
            <p>California residents have additional rights, including:</p>
            <ul>
                <li><strong>Right to Know:</strong> Request details on the categories and specific pieces of personal data collected.</li>
                <li><strong>Right to Delete:</strong> Request deletion of personal data, with certain exceptions.</li>
                <li><strong>Right to Opt-Out of Sale or Sharing of Data:</strong> While HermesReads does not sell personal data, users can opt out of personalized advertising.</li>
                <li><strong>Right to Non-Discrimination:</strong> Exercising privacy rights will not result in unfair treatment.</li>
            </ul>
            <p>To make a request under the CCPA, please use the <a href="/#">Do Not Sell My Information</a> link (if applicable) or contact us directly.</p>
            <h3>6.3 How to Exercise Your Rights</h3>
            <p>To request access, modification, or deletion of your data, please:</p>
            <ul>
                <li>Use our <a href="/#">Privacy Request Form</a> (if available).</li>
                <li>Adjust your <a href="/#">Consent Settings</a> to modify data collection preferences.</li>
            </ul>
            <p>We will respond to verified requests within 30 days (GDPR) or 45 days (CCPA), as required by law.</p>

            <h2 style={{ color: 'var(--primary-color)', marginTop: '1.5rem' }}>7. How to Opt-Out of Data Sharing</h2>
            <p>We respect your right to control your data. If you do not want your information to be shared for analytics or advertising purposes, you can opt out using the following methods:</p>
            <h3>7.1 Opting Out of Website Tracking</h3>
            <p>You can manage your data preferences by:</p>
            <ul>
                <li>Using our <a href="/#">Consent Settings</a> to disable tracking and personalized advertising.</li>
                <li>Enabling "Do Not Track" (DNT) in your browser settings. While we respect DNT signals where possible, some third-party services may not.</li>
            </ul>
            <h3>7.2 Opting Out of Targeted Advertising</h3>
            <p>To disable personalized ads served by third parties, you can:</p>
            <ul>
                <li>Use Google's <a href="https://adssettings.google.com/authenticated" target="_blank" rel="noopener noreferrer">Ad Preferences Manager</a>.</li>
                <li>Opt out via the <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer">Network Advertising Initiative</a>.</li>
                <li>Manage preferences with the <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance (DAA)</a>.</li>
            </ul>
            <h3>7.3 Opting Out of Google Analytics</h3>
            <p>Google Analytics collects anonymous usage data to help improve HermesReads. You can opt out by:</p>
            <ul>
                <li>Installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-Out Browser Add-On</a>.</li>
                <li>Adjusting your Google privacy settings to limit data collection.</li>
            </ul>
            <h3>7.4 Deleting Your Data</h3>
            <p>If you wish to have your personal data removed from our records, you may:</p>
            <ul>
                <li>Contact us directly via the contact form in the header</li>
            </ul>
            <p>Please note that deleting your data may affect certain website functionalities, such as content recommendations.</p>

            <h2 style={{ color: 'var(--primary-color)', marginTop: '1.5rem' }}>8. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time to reflect changes in our data practices, legal requirements, or technological advancements. Any modifications will be posted on this page with an updated "Last Updated" date.</p>
            <h3>8.1 Notification of Changes</h3>
            <p>If we make significant changes to how we collect, use, or share your data, we will notify you by:</p>
            <ul>
                <li>Posting a prominent notice on our homepage.</li>
                <li>Sending an email notification (if applicable).</li>
                <li>Updating the "Last Updated" date at the top of this policy.</li>
            </ul>
            <h3>8.2 Reviewing the Updates</h3>
            <p>We encourage users to periodically review this Privacy Policy to stay informed about our data protection practices. Continued use of HermesReads after policy updates constitutes acceptance of the revised terms.</p>
            <h3>8.3 Questions About Changes</h3>
           <p>If you have any questions or concerns about this Privacy Policy, please contact us at via the form found in header of the page.</p>
        </main>
    </>
);

export default PrivacyPolicy;